import { Form, Head } from "@inertiajs/react"
import { Cloud, HardDrive, Lock, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import Heading from "@/components/heading"
import InputError from "@/components/input-error"
import MultiplePathsInput from "@/components/multiple-paths-input"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/app-layout"
import {
  appBackupConfigsPath,
  appPath,
  newAppBackupConfigPath,
  serversPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { App } from "@/types"

interface BackupConfigNewProps {
  app: Pick<App, "id" | "name" | "db_type" | "db_name" | "location">
}

export default function BackupConfigNew({ app }: BackupConfigNewProps) {
  const [backupType, setBackupType] = useState<"directory" | "database">(
    "directory",
  )
  const [frequency, setFrequency] = useState<"daily" | "weekly" | "monthly">("daily")
  const [scheduleTimes, setScheduleTimes] = useState<string[]>(["04:00"])
  const [saveToS3, setSaveToS3] = useState(false)
  const [keepLocalCopy, setKeepLocalCopy] = useState(false)
  const [useCustomS3Settings, setUseCustomS3Settings] = useState(false)
  const [destinationPath, setDestinationPath] = useState("")

  function addScheduleTime() {
    setScheduleTimes([...scheduleTimes, "04:00"])
  }

  function removeScheduleTime(index: number) {
    setScheduleTimes(scheduleTimes.filter((_, i) => i !== index))
  }

  function updateScheduleTime(index: number, value: string) {
    const newTimes = [...scheduleTimes]
    newTimes[index] = value
    setScheduleTimes(newTimes)
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: app.name, href: appPath(app.id) },
    { title: "New Backup Config", href: newAppBackupConfigPath(app.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Backup Configuration" />

      <div className="p-6">
        <Heading
          title="New Backup Configuration"
          description={`Configure automated backups for ${app.name}`}
        />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>Backup Settings</CardTitle>
            <CardDescription>
              Define what to backup and where to store it
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form
              method="post"
              action={appBackupConfigsPath(app.id)}
              className="space-y-6"
            >
              {({ processing, errors }) => (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="backup_type">Backup Type *</Label>
                      <select
                        id="backup_type"
                        name="backup_config[backup_type]"
                        required
                        value={backupType}
                        onChange={(e) =>
                          setBackupType(
                            e.target.value as "directory" | "database",
                          )
                        }
                        className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                      >
                        <option value="directory">Directory</option>
                        <option value="database">Database</option>
                      </select>
                      <InputError
                        messages={errors["backup_config.backup_type"]}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="frequency">Frequency *</Label>
                      <select
                        id="frequency"
                        name="backup_config[frequency]"
                        required
                        value={frequency}
                        onChange={(e) => setFrequency(e.target.value as "daily" | "weekly" | "monthly")}
                        className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      <InputError
                        messages={errors["backup_config.frequency"]}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label>Backup Schedule Times *</Label>
                    <p className="text-xs text-muted-foreground">
                      Add one or more times when backups should run
                    </p>
                    {scheduleTimes.map((time, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          type="time"
                          value={time}
                          onChange={(e) => updateScheduleTime(index, e.target.value)}
                          name="backup_config[schedule_times][]"
                          required
                          className="flex-1"
                        />
                        {scheduleTimes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeScheduleTime(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addScheduleTime}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Time
                    </Button>
                    <InputError
                      messages={errors["backup_config.schedule_times"]}
                    />
                  </div>

                  {frequency === "daily" && (
                    <div className="grid gap-2">
                      <Label htmlFor="rotation_day_of_week">Weekly Rotation Day</Label>
                      <select
                        id="rotation_day_of_week"
                        name="backup_config[rotation_day_of_week]"
                        defaultValue="0"
                        className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                      >
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Day when a daily backup gets promoted to weekly tier
                      </p>
                      <InputError
                        messages={errors["backup_config.rotation_day_of_week"]}
                      />
                    </div>
                  )}

                  {(frequency === "daily" || frequency === "weekly") && (
                    <div className="grid gap-2">
                      <Label htmlFor="rotation_day_of_month">Monthly Rotation Day</Label>
                      <Input
                        id="rotation_day_of_month"
                        name="backup_config[rotation_day_of_month]"
                        type="number"
                        min="1"
                        max="28"
                        defaultValue="1"
                      />
                      <p className="text-xs text-muted-foreground">
                        Day of month when a backup gets promoted to monthly tier (1-28)
                      </p>
                      <InputError
                        messages={errors["backup_config.rotation_day_of_month"]}
                      />
                    </div>
                  )}

                  {backupType === "directory" && (
                    <>
                      <MultiplePathsInput
                        label="Source Paths"
                        description="Required for directory backups. Add one or more paths to backup."
                        defaultPaths={app.location ? [app.location] : []}
                        name="backup_config[source_paths]"
                        placeholder="/var/www/myapp"
                        position="top"
                      />
                      <InputError
                        messages={errors["backup_config.source_paths"]}
                      />
                      <MultiplePathsInput
                        label="Exclude Paths"
                        description="Tar exclude patterns (e.g. */tmp/backups, */node_modules, */log). Excludes matching paths from the backup."
                        defaultPaths={[]}
                        name="backup_config[exclude_paths]"
                        placeholder="*/tmp/backups"
                        position="top"
                      />
                      <InputError
                        messages={errors["backup_config.exclude_paths"]}
                      />
                    </>
                  )}

                  <div className="border-t pt-4">
                    <h4 className="mb-2 font-medium">Storage Settings</h4>
                    <p className="text-muted-foreground text-xs mb-4">
                      Choose where to store your backups
                    </p>

                    <ToggleGroup
                      type="single"
                      value={saveToS3 ? "s3" : "local"}
                      onValueChange={(value) => setSaveToS3(value === "s3")}
                      className="mb-6 w-full"
                      variant="outline"
                    >
                      <ToggleGroupItem value="local" className="flex-1">
                        <HardDrive className="mr-2 h-4 w-4" />
                        Local Storage
                      </ToggleGroupItem>
                      <ToggleGroupItem value="s3" className="flex-1">
                        <Cloud className="mr-2 h-4 w-4" />
                        Amazon S3
                      </ToggleGroupItem>
                    </ToggleGroup>

                    <input
                      type="hidden"
                      name="backup_config[destination_type]"
                      value={saveToS3 ? "s3" : "local"}
                    />

                    {!saveToS3 && (
                      <div className="space-y-4 border rounded-md p-4 bg-muted/30">
                        <Label htmlFor="destination_path">Local Destination Path *</Label>
                        <Input
                          id="destination_path"
                          name="backup_config[destination_path]"
                          type="text"
                          required
                          placeholder="/backups/myapp"
                          value={destinationPath}
                          onChange={(e) => setDestinationPath(e.target.value)}
                        />
                        <p className="text-muted-foreground text-xs">
                          Absolute path on server filesystem where backups will be stored
                        </p>
                        <InputError
                          messages={errors["backup_config.destination_path"]}
                        />
                      </div>
                    )}

                    {saveToS3 && (
                      <div className="space-y-4 border rounded-md p-4 bg-muted/30">
                        <div className="grid gap-2">
                          <Label htmlFor="s3_destination_prefix">S3 Destination Path *</Label>
                          <Input
                            id="s3_destination_prefix"
                            name="backup_config[s3_settings][destination_prefix]"
                            type="text"
                            required
                            placeholder="backups/myapp"
                          />
                          <p className="text-muted-foreground text-xs">
                            S3 prefix path where backups will be stored (do not include leading slash)
                          </p>
                          <InputError
                            messages={errors["backup_config.s3_settings.destination_prefix"]}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="keep_local_copy"
                            name="backup_config[s3_settings][keep_local_copy]"
                            value="true"
                            checked={keepLocalCopy}
                            onChange={(e) => setKeepLocalCopy(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <Label htmlFor="keep_local_copy" className="text-sm font-normal">
                            Keep local copy after S3 upload
                          </Label>
                        </div>

                        {keepLocalCopy && (
                          <div className="grid gap-2 border-t pt-4">
                            <Label htmlFor="destination_path_s3">Local Copy Path *</Label>
                            <Input
                              id="destination_path_s3"
                              name="backup_config[destination_path]"
                              type="text"
                              required
                              placeholder="/backups/myapp"
                              value={destinationPath}
                              onChange={(e) => setDestinationPath(e.target.value)}
                            />
                            <p className="text-muted-foreground text-xs">
                              Absolute path where the local copy will be stored
                            </p>
                            <InputError
                              messages={errors["backup_config.destination_path"]}
                            />
                          </div>
                        )}

                        <div className="border-t pt-4">
                          <div className="flex items-center gap-2 mb-4">
                            <input
                              type="checkbox"
                              id="use_custom_s3"
                              checked={useCustomS3Settings}
                              onChange={(e) => setUseCustomS3Settings(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <Label htmlFor="use_custom_s3" className="text-sm font-normal flex items-center gap-2">
                              <Lock className="h-4 w-4" />
                              Use custom S3 settings
                            </Label>
                          </div>
                          <p className="text-muted-foreground text-xs mb-4">
                            Override default AWS credentials and bucket. Leave unchecked to use application defaults.
                          </p>

                          {useCustomS3Settings && (
                            <div className="space-y-4 border-l-2 pl-4">
                              <div className="grid gap-2">
                                <Label htmlFor="s3_bucket">Bucket Name</Label>
                                <Input
                                  id="s3_bucket"
                                  name="backup_config[s3_settings][bucket]"
                                  type="text"
                                  placeholder="my-backup-bucket"
                                />
                                <p className="text-muted-foreground text-xs">
                                  Leave blank to use default bucket
                                </p>
                              </div>

                              <div className="grid gap-2">
                                <Label htmlFor="s3_region">Region</Label>
                                <select
                                  id="s3_region"
                                  name="backup_config[s3_settings][region]"
                                  className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                                >
                                  <option value="">Use default region</option>
                                  <option value="us-east-1">US East (N. Virginia)</option>
                                  <option value="us-east-2">US East (Ohio)</option>
                                  <option value="us-west-1">US West (N. California)</option>
                                  <option value="us-west-2">US West (Oregon)</option>
                                  <option value="eu-west-1">Europe (Ireland)</option>
                                  <option value="eu-central-1">Europe (Frankfurt)</option>
                                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                                  <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
                                  <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                                </select>
                              </div>

                              <div className="grid gap-2">
                                <Label htmlFor="s3_access_key_id">AWS Access Key ID</Label>
                                <Input
                                  id="s3_access_key_id"
                                  name="backup_config[s3_settings][access_key_id]"
                                  type="text"
                                  placeholder="AKIAIOSFODNN7EXAMPLE"
                                />
                                <p className="text-muted-foreground text-xs">
                                  Leave blank to use default credentials
                                </p>
                              </div>

                              <div className="grid gap-2">
                                <Label htmlFor="s3_secret_access_key">AWS Secret Access Key</Label>
                                <Input
                                  id="s3_secret_access_key"
                                  name="backup_config[s3_settings][secret_access_key]"
                                  type="password"
                                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                                />
                                <p className="text-muted-foreground text-xs">
                                  Credentials are encrypted before storage
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="mb-4 font-medium">Retention Settings</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="retention_daily">Daily Backups</Label>
                        <Input
                          id="retention_daily"
                          name="backup_config[retention_daily]"
                          type="number"
                          min="1"
                          defaultValue="5"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="retention_weekly">Weekly Backups</Label>
                        <Input
                          id="retention_weekly"
                          name="backup_config[retention_weekly]"
                          type="number"
                          min="1"
                          defaultValue="3"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="retention_monthly">
                          Monthly Backups
                        </Label>
                        <Input
                          id="retention_monthly"
                          name="backup_config[retention_monthly]"
                          type="number"
                          min="1"
                          defaultValue="3"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="enabled"
                      name="backup_config[enabled]"
                      value="true"
                      defaultChecked
                    />
                    <Label htmlFor="enabled" className="text-sm font-normal">
                      Enable this backup configuration
                    </Label>
                  </div>

                  <div className="flex justify-end gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => history.back()}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing && <Spinner />}
                      Create Configuration
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
